import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  Tooltip,
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Avatar,
  Stack,
  InputAdornment,
  TableSortLabel,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { IProductOffering } from "../../types/products";
import useUpdateProductOffering from "../../services/useUpdateProductOffering";
import useDeleteProductOffering from "../../services/useDeleteProductOffering";
import useBulkDeleteProductOfferings from "../../services/useBulkDeleteProductOfferings";

interface OfferingsListProps {
  offerings: IProductOffering[];
  fairId: number;
  canEdit: boolean;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSortChange: (column: SortColumn) => void;
  onToast: (type: "success" | "error", msg: string) => void;
}

interface EditingOffering {
  id: number;
  price: string;
  cost: string;
  stock: number;
  unlimited_stock: boolean;
  available: boolean;
}

type SortColumn = "product" | "producer";
type SortDirection = "asc" | "desc";

/**
 * Lista de ofertas de produtos com edição inline
 */
const OfferingsList = ({
  offerings,
  fairId,
  canEdit,
  sortColumn,
  sortDirection,
  onSortChange,
  onToast,
}: OfferingsListProps) => {
  const [editingOffering, setEditingOffering] =
    useState<EditingOffering | null>(null);
  const [offeringToDelete, setOfferingToDelete] = useState<number | null>(null);
  const [selectedOfferings, setSelectedOfferings] = useState<Set<number>>(
    new Set(),
  );
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const updateMutation = useUpdateProductOffering();
  const deleteMutation = useDeleteProductOffering();
  const bulkDeleteMutation = useBulkDeleteProductOfferings();

  useEffect(() => {
    setSelectedOfferings(new Set());
  }, [offerings]);

  const startEditing = (offering: IProductOffering) => {
    setEditingOffering({
      id: offering.id,
      price: offering.price,
      cost: offering.cost,
      stock: offering.stock,
      unlimited_stock: offering.unlimited_stock,
      available: offering.available,
    });
  };

  const cancelEditing = () => {
    setEditingOffering(null);
  };

  const saveEditing = () => {
    if (!editingOffering) return;

    // Validações
    const price = parseFloat(editingOffering.price);
    const cost = parseFloat(editingOffering.cost);

    if (isNaN(price) || price <= 0) {
      onToast("error", "Preço deve ser maior que zero");
      return;
    }

    if (isNaN(cost) || cost < 0) {
      onToast("error", "Custo não pode ser negativo");
      return;
    }

    if (cost > price) {
      onToast("error", "Custo não pode ser maior que o preço de venda");
      return;
    }

    if (!editingOffering.unlimited_stock && editingOffering.stock < 0) {
      onToast("error", "Estoque não pode ser negativo");
      return;
    }

    updateMutation.mutate(
      {
        id: editingOffering.id,
        offering: {
          price: editingOffering.price,
          cost: editingOffering.cost,
          stock: editingOffering.stock,
          unlimited_stock: editingOffering.unlimited_stock,
          available: editingOffering.available,
        },
      },
      {
        onSuccess: () => {
          onToast("success", "Oferta atualizada com sucesso!");
          setEditingOffering(null);
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Falha ao atualizar oferta.";
          onToast("error", msg);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!offeringToDelete) return;

    deleteMutation.mutate(offeringToDelete, {
      onSuccess: () => {
        onToast("success", "Produto removido da feira!");
        setOfferingToDelete(null);
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Falha ao remover produto.";
        onToast("error", msg);
        setOfferingToDelete(null);
      },
    });
  };

  const isEditing = (offeringId: number) => editingOffering?.id === offeringId;
  const selectedCount = selectedOfferings.size;
  const allSelected =
    offerings.length > 0 &&
    offerings.every((offering) => selectedOfferings.has(offering.id));

  const toggleSelected = (offeringId: number) => {
    setSelectedOfferings((prev) => {
      const next = new Set(prev);
      if (next.has(offeringId)) {
        next.delete(offeringId);
      } else {
        next.add(offeringId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedOfferings((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        offerings.forEach((offering) => next.delete(offering.id));
      } else {
        offerings.forEach((offering) => next.add(offering.id));
      }
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedCount === 0) return;
    setConfirmBulkDelete(true);
  };

  const confirmDeleteSelected = () => {
    if (selectedCount === 0) return;
    const ids = Array.from(selectedOfferings);
    bulkDeleteMutation.mutate(
      { fairId, ids },
      {
        onSuccess: (data: any) => {
          const count = data?.deleted_count ?? ids.length;
          onToast("success", `${count} produto(s) removido(s) da feira.`);
          setSelectedOfferings(new Set());
          setConfirmBulkDelete(false);
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Falha ao remover produtos.";
          onToast("error", msg);
          setConfirmBulkDelete(false);
        },
      },
    );
  };

  return (
    <>
      {canEdit && selectedCount > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Chip
            label={`${selectedCount} selecionado${selectedCount !== 1 ? "s" : ""}`}
            variant="outlined"
          />
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            disabled={bulkDeleteMutation.isLoading}
          >
            Remover selecionados
          </Button>
        </Stack>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={toggleSelectAll}
                  disabled={!canEdit}
                />
              </TableCell>
              <TableCell width={50}></TableCell>
              <TableCell
                sortDirection={sortColumn === "product" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "product"}
                  direction={sortColumn === "product" ? sortDirection : "asc"}
                  onClick={() => onSortChange("product")}
                >
                  Produto
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortColumn === "producer" ? sortDirection : false
                }
              >
                <TableSortLabel
                  active={sortColumn === "producer"}
                  direction={sortColumn === "producer" ? sortDirection : "asc"}
                  onClick={() => onSortChange("producer")}
                >
                  Produtor
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width={120}>
                Preço (R$)
              </TableCell>
              <TableCell align="right" width={120}>
                Custo (R$)
              </TableCell>
              <TableCell align="center" width={90}>
                Estoque
              </TableCell>
              <TableCell align="center" width={100}>
                Status
              </TableCell>
              {canEdit && (
                <TableCell align="center" width={100}>
                  Ações
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {offerings.map((offering) => {
              const editing = isEditing(offering.id);

              return (
                <TableRow
                  key={offering.id}
                  sx={{
                    opacity: offering.available ? 1 : 0.5,
                    backgroundColor: editing ? "action.hover" : "inherit",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedOfferings.has(offering.id)}
                      onChange={() => toggleSelected(offering.id)}
                      disabled={!canEdit}
                    />
                  </TableCell>
                  {/* Imagem do produto */}
                  <TableCell>
                    <Avatar
                      src={offering.product.image}
                      alt={offering.product.name}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    >
                      {offering.product.name.charAt(0)}
                    </Avatar>
                  </TableCell>

                  {/* Nome do produto */}
                  <TableCell>
                    <Box>
                      <strong>{offering.product.name}</strong>
                      {offering.product.unit && (
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            color: "text.secondary",
                            fontSize: "0.875rem",
                          }}
                        >
                          ({offering.product.unit})
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  {/* Produtor */}
                  <TableCell>{offering.product.producer.name}</TableCell>

                  {/* Preço */}
                  <TableCell align="right">
                    {editing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editingOffering?.price || ""}
                        onChange={(e) =>
                          setEditingOffering((prev) =>
                            prev ? { ...prev, price: e.target.value } : null,
                          )
                        }
                        inputProps={{ step: "0.01", min: "0" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R$</InputAdornment>
                          ),
                        }}
                        sx={{ width: 120 }}
                      />
                    ) : (
                      `R$ ${parseFloat(offering.price).toFixed(2)}`
                    )}
                  </TableCell>

                  {/* Custo */}
                  <TableCell align="right">
                    {editing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editingOffering?.cost || ""}
                        onChange={(e) =>
                          setEditingOffering((prev) =>
                            prev ? { ...prev, cost: e.target.value } : null,
                          )
                        }
                        inputProps={{ step: "0.01", min: "0" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R$</InputAdornment>
                          ),
                        }}
                        sx={{ width: 120 }}
                      />
                    ) : (
                      `R$ ${parseFloat(offering.cost).toFixed(2)}`
                    )}
                  </TableCell>

                  {/* Estoque */}
                  <TableCell align="center">
                    {editing ? (
                      <Stack spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          type="number"
                          value={editingOffering?.stock || 0}
                          onChange={(e) =>
                            setEditingOffering((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    stock: parseInt(e.target.value) || 0,
                                  }
                                : null,
                            )
                          }
                          disabled={editingOffering?.unlimited_stock}
                          inputProps={{ min: "0" }}
                          sx={{ width: 90 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: "0.75rem",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={editingOffering?.unlimited_stock || false}
                            onChange={(e) =>
                              setEditingOffering((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      unlimited_stock: e.target.checked,
                                    }
                                  : null,
                              )
                            }
                          />
                          Ilimitado
                        </Box>
                      </Stack>
                    ) : offering.unlimited_stock ? (
                      <Chip label="Ilimitado" color="primary" size="small" />
                    ) : (
                      <Chip
                        label={offering.stock}
                        color={offering.stock === 0 ? "error" : "default"}
                        size="small"
                      />
                    )}
                  </TableCell>

                  {/* Status (Disponível) */}
                  <TableCell align="center">
                    {editing ? (
                      <Checkbox
                        checked={editingOffering?.available || false}
                        onChange={(e) =>
                          setEditingOffering((prev) =>
                            prev
                              ? { ...prev, available: e.target.checked }
                              : null,
                          )
                        }
                      />
                    ) : offering.available ? (
                      <Chip
                        label="Disponível"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="Indisponível"
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>

                  {/* Ações */}
                  {canEdit && (
                    <TableCell align="center">
                      {editing ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="Salvar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={saveEditing}
                              disabled={updateMutation.isLoading}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              color="default"
                              onClick={cancelEditing}
                              disabled={updateMutation.isLoading}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => startEditing(offering)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setOfferingToDelete(offering.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={!!offeringToDelete}
        onClose={() => setOfferingToDelete(null)}
      >
        <DialogTitle>Remover Produto?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover este produto da feira? Esta ação não
            pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOfferingToDelete(null)}
            disabled={deleteMutation.isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
      >
        <DialogTitle>Remover Produtos Selecionados?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover {selectedCount} produto
            {selectedCount !== 1 ? "s" : ""} da feira? Esta ação não pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmBulkDelete(false)}
            disabled={bulkDeleteMutation.isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDeleteSelected}
            color="error"
            variant="contained"
            disabled={bulkDeleteMutation.isLoading}
          >
            {bulkDeleteMutation.isLoading ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OfferingsList;
